/**
 * Subcollection image nav
 * - CLS-safe, scroll memory, iOS quirks
 * - Fade masks controlled by classes: no-fade / no-left-fade / no-right-fade
 */

(function () {
  const instances = new WeakMap();
  /* -----------------------------------------------
   * Helpers & Environment
  -------------------------------------------------*/
  const IS_APPLE_MOBILE =
    /iP(ad|hone|od)/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  const $ = (s, r = document) => r.querySelector(s);

  function isParentPage(nav) {
    // If parent pill not rendered, this *is* the parent page
    return !nav.querySelector('[data-is-parent="true"]');
  }

  function pillIsClipped(nav, el) {
    if (!nav || !el) return false;
    const nr = nav.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    return er.left < nr.left || er.right > nr.right;
  }

  // Ensure active pill stays in view without CLS jumps
  function ensurePillVisible(nav, el, { animate = false } = {}) {
    if (!nav || !el) return;
    if (!pillIsClipped(nav, el)) return;

    const prev = nav.style.scrollBehavior;
    nav.style.scrollBehavior = "auto";

    try {
      const nr = nav.getBoundingClientRect();
      const er = el.getBoundingClientRect();
      // Move only the tile strip, never its ancestors or the page viewport.
      nav.scrollTo({
        left: nav.scrollLeft + (IS_APPLE_MOBILE
          ? er.left - nr.left
          : (er.left + er.right) / 2 - (nr.left + nr.right) / 2),
        behavior: "instant"
      });
    } catch {
      const nr = nav.getBoundingClientRect();
      const er = el.getBoundingClientRect();
      nav.scrollLeft += (er.left + er.right) / 2 - (nr.left + nr.right) / 2;
    }

    nav.style.scrollBehavior = prev || "";

    if (animate && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      requestAnimationFrame(() => {
        const nr = nav.getBoundingClientRect();
        const er = el.getBoundingClientRect();
        nav.scrollTo({
          left: nav.scrollLeft + (er.left + er.right) / 2 - (nr.left + nr.right) / 2,
          behavior: "smooth"
        });
      });
    }
  }

  /* -----------------------------------------------
   * Init
  -------------------------------------------------*/
  function initImagePills({ skipAutoScroll = false } = {}) {
    const nav = $(".subcollection-nav-img"); // scroll element w/ mask
    const wrapper = $(".subcollection-nav-img-wrapper"); // layout container
    const carrier = $("[data-collection-group]");
    if (!nav || !wrapper || !carrier) return;
    if (instances.has(nav)) {
      requestAnimationFrame(instances.get(nav));
      return;
    }

    // Prevent initial flash of left fade
    nav.classList.add("no-left-fade");
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        applyMaskState();
        nav.classList.remove("no-left-fade"); // JS takes over
      });
    });

    if (nav.scrollWidth > nav.clientWidth) {
      nav.classList.add("is-scrolling");
    }
/* -------------------------------------------
 * Fade Mask Logic — Phase 1 fix
--------------------------------------------*/
const applyMaskState = () => {
  const overflow = nav.scrollWidth > nav.clientWidth + 1;

  // No overflow? No fades at all.
  if (!overflow) {
    nav.classList.add("no-fade");
    nav.classList.remove("no-left-fade", "no-right-fade");
    return;
  }

  nav.classList.remove("no-fade");

  // Precision scroll edge checks
  const buffer = 2; // tolerance for fractional scroll & iOS bounce
  const maxScroll = nav.scrollWidth - nav.clientWidth - buffer;

  const atLeft = nav.scrollLeft <= buffer;
  const atRight = nav.scrollLeft >= maxScroll;

  // Reset fades each frame
  nav.classList.remove("no-left-fade", "no-right-fade");

  if (atLeft && !atRight) {
    // Far left only
    nav.classList.add("no-left-fade");
  } else if (atRight && !atLeft) {
    // Far right only (keep left fade ON)
    nav.classList.add("no-right-fade");
  } else {
    // Middle scrolling — both fades visible (do nothing)
  }
};
instances.set(nav, applyMaskState);


    /* -------------------------------------------
     * Session memory keys
    --------------------------------------------*/
    const groupKey = carrier.dataset.collectionGroup;
    const storage = sessionStorage;
    const kX = `subcatImg_scroll_${groupKey}`;
    const kY = `subcatImg_scrollY_${groupKey}`;
    const kClicked = `subcatImg_clicked_${groupKey}`;
    const kLast = "subcatImg_lastGroup";

    const last = storage.getItem(kLast);
    storage.setItem(kLast, groupKey);

    try {
      Object.keys(storage).forEach(key => {
        const mine =
          key.startsWith("subcatImg_scroll_") ||
          key.startsWith("subcatImg_scrollY_") ||
          key.startsWith("subcatImg_clicked_");

        if (mine) {
          const g = key.split("_").pop();
          if (g !== groupKey && g !== last) storage.removeItem(key);
        }
      });
    } catch {}

    /* -------------------------------------------
     * Scroll restore logic
    --------------------------------------------*/
    const active = nav.querySelector(".subcollection-nav-img__item.is-active");
    const onParent = isParentPage(nav);
    const cameFromClick = storage.getItem(kClicked) === "1";
    const savedX = storage.getItem(kX);

    if (onParent) {
      // Reset when returning to parent
      nav.scrollLeft = 0;
      storage.removeItem(kClicked);
      storage.removeItem(kX);
      storage.removeItem(kY);
      requestAnimationFrame(applyMaskState);
    } else if (!skipAutoScroll) {
      if (cameFromClick && savedX !== null) {
        const apply = () => {
          nav.scrollLeft = parseInt(savedX, 10);
          ensurePillVisible(nav, active, { animate: true });
          storage.removeItem(kClicked);
          applyMaskState();
        };

        IS_APPLE_MOBILE
          ? requestAnimationFrame(() => setTimeout(apply, 60))
          : requestAnimationFrame(apply);
      } else {
        const run = () => {
          ensurePillVisible(nav, active, { animate: false });
          applyMaskState();
        };

        if (document.readyState === "complete") requestAnimationFrame(run);
        else window.addEventListener("load", run, { once: true });
      }
    } else {
      requestAnimationFrame(applyMaskState);
    }

    /* -------------------------------------------
     * Click = remember scroll pos
    --------------------------------------------*/
    nav.addEventListener("click", e => {
      const item = e.target.closest(".subcollection-nav-img__item");
      if (!item) return;

      const clickedParent = item.hasAttribute("data-is-parent");

      requestAnimationFrame(() => {
        storage.setItem(kClicked, "1");

        if (clickedParent) {
          storage.removeItem(kX);
          storage.removeItem(kY);
        } else {
          storage.setItem(kX, nav.scrollLeft);
        }
      });
    });

    /* -------------------------------------------
     * Listeners
    --------------------------------------------*/
    wrapper.addEventListener("scroll", applyMaskState, { passive: true });
    nav.addEventListener("scroll", applyMaskState, { passive: true });
    window.addEventListener(
      "resize",
      () => requestAnimationFrame(applyMaskState),
      { passive: true }
    );

    // Final initial state
    requestAnimationFrame(() => {
      requestAnimationFrame(applyMaskState);
    });
  }

  /* -----------------------------------------------
   * Boot
  -------------------------------------------------*/
  function boot({ skipAutoScroll = false } = {}) {
    if (!document.querySelector(".subcollection-nav-img")) return;
    initImagePills({ skipAutoScroll });
  }

  if (document.readyState === "complete") boot();
  else window.addEventListener("load", () => boot(), { once: true });

  window.addEventListener("pageshow", e => {
    if (e.persisted) boot({ skipAutoScroll: true });
  });
})();